import { EnrollmentApi } from '../../services/enrollment.service'
import type { EnrollmentData } from '../../types/enrollment.types'
import { buildStepPayload, createSaveStepPayload } from './enrollmentDataUtils'

/**
 * Session management utilities for enrollment wizard
 * ✅ 100% DYNAMIC - Works for ALL campaigns
 */
export class SessionManager {
  private sessionId: string | null
  private setSessionId: (id: string | null) => void
  private enrollmentDataRef: React.MutableRefObject<EnrollmentData>
  private skipResumeOnceRef: React.MutableRefObject<boolean>
  private movedToPendingRef: React.MutableRefObject<boolean>
  private isRestoringSessionRef: React.MutableRefObject<boolean>
  private lastValidityCheck: number = 0

  constructor(
    sessionId: string | null,
    setSessionId: (id: string | null) => void,
    enrollmentDataRef: React.MutableRefObject<EnrollmentData>,
    skipResumeOnceRef: React.MutableRefObject<boolean>,
    movedToPendingRef: React.MutableRefObject<boolean>,
    isRestoringSessionRef: React.MutableRefObject<boolean>
  ) {
    this.sessionId = sessionId
    this.setSessionId = setSessionId
    this.enrollmentDataRef = enrollmentDataRef
    this.skipResumeOnceRef = skipResumeOnceRef
    this.movedToPendingRef = movedToPendingRef
    this.isRestoringSessionRef = isRestoringSessionRef
  }

  /**
   * Initialize session from localStorage
   */
  static initializeSessionId(): string | null {
    try {
      return localStorage.getItem('enrollmentSessionId')
    } catch {
      return null
    }
  }

  /**
   * Auto-complete previous steps based on current step index
   * ✅ Campaign-agnostic - works for all campaigns
   */
  private autoCompletePreviousSteps(existingCompletedSteps: number[], currentStepIndex: number): number[] {
    const completedSteps = [...existingCompletedSteps]

    // Auto-complete all steps from 0 to currentStepIndex (exclusive)
    for (let i = 0; i < currentStepIndex; i++) {
      if (!completedSteps.includes(i)) {
        completedSteps.push(i)
      }
    }

    return completedSteps.sort((a, b) => a - b)
  }

  /**
   * Save session ID to localStorage
   */
  saveSessionIdToStorage(sessionId: string): void {
    try {
      localStorage.setItem('enrollmentSessionId', sessionId)
    } catch {
      // Ignore localStorage errors
    }
  }

  /**
   * Remove session ID from localStorage
   */
  removeSessionIdFromStorage(): void {
    try {
      localStorage.removeItem('enrollmentSessionId')
    } catch {
      // Ignore localStorage errors
    }
  }

  /**
   * Remove all enrollment data from localStorage
   */
  removeAllEnrollmentDataFromStorage(): void {
    try {
      localStorage.removeItem('enrollmentSessionId')
      localStorage.removeItem('enrollmentCurrentStep')
      localStorage.removeItem('enrollmentCampaign')
      localStorage.removeItem('enrollmentCampaignName')
      localStorage.removeItem('pendingResumeStep')
    } catch {
      // Ignore localStorage errors
    }
  }

  /**
 * Save current step data and return session info.
 * Pass newStepIndex to advance currentStepIndex in one write (avoids a separate updateCurrentStepIndex call).
 */
  async saveCurrentStep(
    currentStepIndex: number,
    stepId: string,
    newStepIndex?: number
  ): Promise<{ id?: string } | null> {
    const snapshot = this.enrollmentDataRef.current

    const databaseCampaignId = (snapshot as any).campaign?.id
    const campaignId = snapshot.campaignId || snapshot.selectedCampaign || 'video-game-addiction'

    const payload = buildStepPayload(stepId, snapshot, databaseCampaignId || campaignId)

    // Use newStepIndex when provided so the DB advances to the target step in the same write
    const stepIndexForSave = newStepIndex ?? currentStepIndex

    try {
      const saved = await EnrollmentApi.saveStepAuto(
        createSaveStepPayload(this.sessionId || undefined, stepId, stepIndexForSave, payload)
      )

      if (!this.sessionId && saved?.id) {
        this.setSessionId(saved.id)
        this.saveSessionIdToStorage(saved.id)
        this.skipResumeOnceRef.current = true
      }

      return saved
    } catch (e: any) {
      console.error('Failed saving step', e)
      // Extract error message from API response
      const errorMessage = e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Failed to save step'
      // Throw with the actual error message so it can be displayed to the user
      throw new Error(errorMessage)
    }
  }

  /**
   * Set campaign ID - campaign is stored in enrollment data only, NOT as a step
   * ✅ 100% Dynamic - works for all campaigns
   */
  async setCampaignId(campaignId: string): Promise<void> {

    this.enrollmentDataRef.current.campaignId = campaignId
    this.enrollmentDataRef.current.selectedCampaign = campaignId

  }

  /**
   * Update only the currentStepIndex without saving step data
   */
  async updateCurrentStepIndex(stepIndex: number): Promise<{ id?: string } | null> {
    if (!this.sessionId) {
      console.error('No session ID available for updating step index')
      return null
    }

    const stepId = `step${stepIndex + 1}`

    try {
      const saved = await EnrollmentApi.saveStepAuto({
        sessionId: this.sessionId,
        stepId: stepId,
        stepIndex: stepIndex,
        stepData: {},
        enrollmentType: 'CLIENT_ENROLLMENT'
      })

      return saved
    } catch (e) {
      console.error('Failed updating step index', e)
      return null
    }
  }

  /**
   * Resume session from server
   * ✅ FIXED: Simplified, no nested step flattening, 100% dynamic
   */
  async resumeSession(
    setEnrollmentData: (updater: (prev: EnrollmentData) => EnrollmentData) => void,
    setCurrentWizardStepIndex: (index: number) => void,
    setIsCompletedMode: (completed: boolean) => void,
    resetEnrollment: () => void,
    isCompletedMode: boolean
  ): Promise<void> {
    if (!this.sessionId || this.isRestoringSessionRef.current) {
      return
    }

    this.skipResumeOnceRef.current = false

    try {
      this.isRestoringSessionRef.current = true

      const session = await EnrollmentApi.getSession(this.sessionId)

      if (!session) {
        if (!isCompletedMode) {
          resetEnrollment()
        }
        return
      }


      if (session.status === 'COMPLETED') {
        setIsCompletedMode(true)
      }

      // ✅ Refresh campaign data from server to get latest minAge/maxAge settings
      let freshCampaignData: any = null
      const campaignId = session.campaignId || (session.data as any)?.campaignId
      if (campaignId) {
        try {
          const { CampaignService } = await import('../../../campaigns/services')
          const campaignResponse = await CampaignService.getCampaignById(campaignId)
          freshCampaignData = campaignResponse.data || campaignResponse
        } catch (error) {
          console.warn('⚠️ Failed to refresh campaign data:', error)
        }
      }

      // Restore the data
      if (session?.data) {

        await new Promise<void>((resolve) => {
          setEnrollmentData(prev => {
            const stepData = session.data as any


            // ✅ Get campaign ID from session
            const campaignId = session.campaignId || stepData.campaignId || stepData.selectedCampaign


            // ✅ FIXED: Detect and flatten nested step data
            // Check if data has nested steps (step1, step2, etc. OR campaign step IDs like agent-consents)
            const numericStepPattern = /^step\d+$/
            const knownStepIds = [
              'client-validation', 'enrollment-data', 'employment-plan', 'agent-consents',
              'compliance', 'follow-up', 'gaming-platform', 'intake-contact', 'injured-party',
              'gaming-details', 'health-legal', 'emergency-notes', 'identity-preview', 'submit', 'signature'
            ]

            const hasNestedSteps = Object.keys(stepData).some(key =>
              (numericStepPattern.test(key) || knownStepIds.includes(key)) &&
              typeof stepData[key] === 'object' && stepData[key] !== null
            )

            let flattenedData: any = {}

            if (hasNestedSteps) {
              // Flatten nested step data (both step1/step2 format and agent-consents format)
              Object.keys(stepData).forEach(key => {
                const isStepKey = numericStepPattern.test(key) || knownStepIds.includes(key)
                if (isStepKey && typeof stepData[key] === 'object' && stepData[key] !== null) {
                  // Merge step data into root level
                  flattenedData = {
                    ...flattenedData,
                    ...stepData[key]
                  }
                } else {
                  // Keep non-step keys as-is
                  flattenedData[key] = stepData[key]
                }
              })

            } else {
              flattenedData = stepData
            }

            // ✅ Merge flattened data with previous state
            // IMPORTANT: Preserve driversLicense from prev if it exists, to avoid overwriting during recovery
            const restoredData: any = {
              ...prev,
              ...flattenedData,
              campaignId: campaignId,
              selectedCampaign: stepData.selectedCampaign || campaignId,
              campaign: freshCampaignData || stepData.campaign || prev.campaign
            }

            // ✅ PRESERVE driversLicense from prev if it exists and has a URL
            // This prevents the URL from disappearing during session recovery
            // Only overwrite if flattenedData actually has a driversLicense with a URL
            if (prev.driversLicense?.url && (!flattenedData.driversLicense?.url)) {
              restoredData.driversLicense = prev.driversLicense
            }

            // ✅ Map step2.identity to driversLicense for Roblox campaigns
            // This handles Driver's License uploaded via opt-in email
            // Check campaign name (most reliable) - database campaign IDs are UUIDs/CUIDs, not hardcoded strings
            const campaignName = restoredData.campaign?.name || stepData.campaign?.name || ''
            const isRobloxCampaign = campaignName.toLowerCase().includes('roblox') ||
              stepData.selectedCampaign === 'robloxExploitation'


            // ✅ Map step2.identity to driversLicense for Roblox campaigns
            // Priority: step2.identity (from opt-in email) > existing driversLicense (from STEP 5 upload)
            // Only map if driversLicense doesn't already exist with a URL
            if (isRobloxCampaign && stepData.step2?.identity) {
              const identityData = stepData.step2.identity
              // Map if: (1) identity data exists AND (2) driversLicense doesn't have a URL (don't overwrite existing)
              if ((identityData.url || identityData.name || identityData.fileName) && !restoredData.driversLicense?.url) {
                restoredData.driversLicense = {
                  url: identityData.url,
                  name: identityData.fileName || identityData.name || 'Driver\'s License', // ✅ Add fallback
                  fileName: identityData.fileName || identityData.name, // ✅ Also set fileName for compatibility
                  mimeType: identityData.mimeType,
                  size: identityData.size,
                  uploadedAt: identityData.uploadedAt,
                  consentAgreed: identityData.consentAgreed,
                }
              }
            }

            // ✅ Restore session-level metadata
            restoredData.idDocumentUrl = (session as any).idDocumentUrl || prev.idDocumentUrl
            restoredData.identityConsentAgreed = (session as any).consentAgreed || prev.identityConsentAgreed
            restoredData.emailLinkVerified = (session as any).emailLinkVerified || prev.emailLinkVerified

            // ✅ Restore completion tracking from session
            const sessionCompletedSteps = session.completedSteps || []
            const dataCompletedSteps = flattenedData.completedSteps || []
            const allCompletedSteps = Array.from(new Set([...sessionCompletedSteps, ...dataCompletedSteps]))

            // ✅ Auto-complete all steps BEFORE the current step
            restoredData.completedSteps = this.autoCompletePreviousSteps(
              allCompletedSteps,
              session.currentStepIndex || 0
            )

            // ✅ Restore skipped steps
            const sessionSkippedSteps = (session as any).skippedSteps || []
            const dataSkippedSteps = flattenedData.skippedSteps || []
            restoredData.skippedSteps = Array.from(new Set([...sessionSkippedSteps, ...dataSkippedSteps]))



            setTimeout(() => resolve(), 0)

            return restoredData
          })
        })

        // Wait for state to propagate
        await new Promise(resolve => setTimeout(resolve, 50))

      }

      // Navigate to the correct step
      if (session?.currentStepIndex !== undefined) {
        if (this.skipResumeOnceRef.current) {
          this.skipResumeOnceRef.current = false
        } else {
          // ✅ FIXED: Support any number of steps, don't cap at 9
          const targetStepIndex = session.currentStepIndex || 0
          setCurrentWizardStepIndex(targetStepIndex)
        }
      }

    } catch (e) {
      console.error('❌ Failed to resume enrollment session', e)
      if (!isCompletedMode) {
        resetEnrollment()
      }
    } finally {
      this.isRestoringSessionRef.current = false
    }
  }

  /**
   * Move session to pending status
   */
  async moveToPending(currentStepIndex: number): Promise<void> {
    if (!this.sessionId || this.movedToPendingRef.current) {
      return
    }

    this.movedToPendingRef.current = true

    try {
      const stepId = this.getStepIdFromIndex(currentStepIndex)
      const snapshot = this.enrollmentDataRef.current
      const stepData = buildStepPayload(stepId, snapshot)

      await EnrollmentApi.saveStepAuto({
        sessionId: this.sessionId,
        stepId,
        stepIndex: currentStepIndex,
        stepData,
      })

      await EnrollmentApi.moveToPending(this.sessionId)

    } catch (err) {
      console.error('Failed to move enrollment session to pending', err)
    }
  }

  /**
   * Send email link for step 2
   */
  async sendEmailLink(clientEmail: string): Promise<void> {
    if (!clientEmail) {
      throw new Error('Please enter a client email address first.')
    }

    const saved = await EnrollmentApi.saveStepAuto({
      sessionId: this.sessionId || undefined,
      stepId: 'step2',
      stepIndex: 1,
      stepData: buildStepPayload('step2', this.enrollmentDataRef.current),
    })

    if (!this.sessionId && saved?.id) {
      this.setSessionId(saved.id)
      this.saveSessionIdToStorage(saved.id)
    }

    const activeSessionId = saved?.id || this.sessionId
    if (!activeSessionId) {
      throw new Error('Could not create an enrollment session')
    }

    await EnrollmentApi.sendStep2Email(activeSessionId, clientEmail)
  }

  /**
   * Complete the current session
   */
  async completeSession(currentStepIndex: number): Promise<void> {
    if (!this.sessionId) {
      return
    }

    try {
      const stepId = this.getStepIdFromIndex(currentStepIndex)
      const snapshot = this.enrollmentDataRef.current
      const stepData = buildStepPayload(stepId, snapshot)

      await EnrollmentApi.saveStepAuto({
        sessionId: this.sessionId,
        stepId,
        stepIndex: currentStepIndex,
        stepData,
      })

      await EnrollmentApi.complete(this.sessionId)
    } catch (e) {
      console.error('Failed to complete enrollment', e)
      throw e
    }
  }

  /**
   * Validate step access with backend
   */
  async validateStepAccess(targetIndex: number): Promise<{ success: boolean; error?: string; message?: string }> {
    if (!this.sessionId) {
      return { success: true }
    }

    try {
      return await EnrollmentApi.validateStepAccess(this.sessionId, targetIndex)
    } catch (error) {
      console.error('Failed to validate step access:', error)
      return { success: true }
    }
  }

  /**
   * Get the timestamp of the last validity check
   */
  getLastValidityCheck(): number {
    return this.lastValidityCheck
  }

  /**
   * Check if session is still valid
   */
  async checkSessionValidity(): Promise<boolean> {
    if (!this.sessionId || this.isRestoringSessionRef.current) {
      return true
    }

    try {
      this.lastValidityCheck = Date.now()
      const session = await EnrollmentApi.getSession(this.sessionId)
      return !(!session || session.status === 'COMPLETED')
    } catch {
      return false
    }
  }

  /**
   * Get step ID from step index
   */
  private getStepIdFromIndex(stepIndex: number): string {
    const stepIds = Array.from({ length: 20 }, (_, i) => `step${i + 1}`)
    return stepIds[stepIndex] || 'step1'
  }

  /**
   * Reset session state
   */
  resetSessionState(): void {
    this.setSessionId(null)
    this.movedToPendingRef.current = false
    this.skipResumeOnceRef.current = false
    this.isRestoringSessionRef.current = false
    this.removeAllEnrollmentDataFromStorage()
  }
}